import {castArray, uniqueArray} from '@coolcolduk/util';
import {useCallback, useState} from 'react';
import {ModuleTopic} from '@cp/model';
import mapTopicStringToTopic from './map/mapTopicStringToTopic';
import filterTopicStringChild from './filter/filterTopicStringChild';
import useTopicSelectionSelectedItems from './useTopicSelectionSelectedItems';
import useTopicSelectionTreeObject from './useTopicSelectionTreeObject';

export interface UseTopicSelectionParams {
  moduleTopics: ModuleTopic[];
  /** topic only without category */
  selectedTopicsOnly: string[];
  onSelectionChange: (topics: string[]) => void;
  disabled?: boolean;
}

// export interface UseTopicSelectionResult {
//   treeObject: TopicTreeObject[];
//   defaultExpandedItems: string[];
//   internalSelectedTopics: string[];
//   isAllSelected: boolean;
//   isIndeterminate: boolean;
//   handleToggleAll: () => void;
//   handleTreeSelectionChange: (event: React.SyntheticEvent | null, newSelection: string | string[] | null) => void;
// }

export default function useTopicSelection(params: UseTopicSelectionParams) {
  const {moduleTopics, selectedTopicsOnly, onSelectionChange, disabled = false} = params;

  const {treeObject, allParentTopicStrings, allChildTopicStrings, allTopicStrings} =
    useTopicSelectionTreeObject(moduleTopics);

  // Internal state to track selection including parents
  const [internalSelectedTopics, setInternalSelectedTopics] = useState<string[]>([]);
  useTopicSelectionSelectedItems(moduleTopics, selectedTopicsOnly, allChildTopicStrings, setInternalSelectedTopics);

  // Update parent component when internal selection changes (without parents)
  const handleInternalSelectionChange = useCallback(
    (topicStrings: string[]) => {
      if (disabled) return;
      setInternalSelectedTopics(topicStrings);
      const topicsWithoutParents = topicStrings.filter(filterTopicStringChild);
      onSelectionChange(topicsWithoutParents.map(mapTopicStringToTopic));
    },
    [disabled, onSelectionChange],
  );

  const handleTreeSelectionChange = useCallback(
    (_event: React.SyntheticEvent | null, newSelection: string | string[] | null) => {
      if (disabled) return;
      if (newSelection === null) {
        handleInternalSelectionChange([]);
        return;
      }
      const selectionArray = castArray(newSelection);
      handleInternalSelectionChange(uniqueArray(selectionArray));
    },
    [disabled, handleInternalSelectionChange],
  );

  // toggle on by default, off if all is already on
  const handleToggleAll = useCallback(() => {
    if (allTopicStrings.length > 0 && internalSelectedTopics.length === allTopicStrings.length) {
      handleInternalSelectionChange([]);
    } else {
      handleInternalSelectionChange(allTopicStrings);
    }
  }, [allTopicStrings, internalSelectedTopics.length, handleInternalSelectionChange]);

  return {
    treeObject,
    allParentTopicStrings,
    allChildTopicStrings,
    allTopicStrings,
    internalSelectedTopics,
    handleToggleAll,
    handleTreeSelectionChange,
    handleInternalSelectionChange,
  };
}
